/******************************************
*  Author : Riccardo Di Dato   
*  Created On : Tue May 17 2022
*  File : APIClient.ts
*******************************************/


import { Response, Utils } from '@ciaopeople/cp-shatsu/HTTP';
import { Logger } from '@ciaopeople/cp-shatsu/Logging';
import { isObject, isString } from '@ciaopeople/cp-shatsu/TypeChecking';
import { LayeredAuthenticatedClient } from '../../httpClient/LayeredAuthenticatedClient.js';
import { LayeredHTTPClientConfig } from '../../httpClient/LayeredHTTPClientConfig.js';
import { ThirdParyClientAuthDataForSignIn } from '../../login/data/LoginData.js';
import { MembershipCardData } from '../../membership/MembershipCardData.js';
import { MembershipType } from "../../membership/MembershipController.js";
import { BuyMembershipOptions, MembershipUpsellOptions, OTPVerificationResponse } from '../../sdk/ClientSdkPublicInterface.js';
import { DeviceAction } from "../../stats/UserActionTypes.js";
import { APIClientInterface, Ask1stPartyEmailLoginOperation, MembershipDetails } from '../APIClientInterface.js';
import { RESTAPIEventsBaseImpl } from '../RESTAPIEvents.js';
import { AllRESTUserServicesMethods, EmailLoginAskResponse, FullContent } from '../RESTUserServices.js';
import { DataDeletionInfo } from '../types/AuthDataTypes.js';
import { BookmarkCheckRequest, BookmarkCheckResponse, BookmarkQueryObj, BookmarkedContentsResponse } from '../types/BookmarkingDataTypes.js';
import { MembershipStatusInfo } from '../types/MembershipStatusInfo.js';
import { ContentRestrictionCriteria, FollowResponseObject, GroupedResolvedTopicObjs, GroupedTopicObjs, RecommendedCount, RecommendedResponse, TopicFollowCheckRequest, TopicFollowCheckResponse, TopicObj, TopicQueryObj, TopicSubscriptionCheckRequest, TopicSubscriptionCheckResponse, UserActionExtraInfo } from '../types/NotificationDataTypes.js';
import { DeviceOrientedRequest, DeviceUpdateRequestPayload, SubscriptionListResponse, isDeviceUpdateRequestPayload, isFirebaseDeviceRequestPayload } from '../types/SubscriptionTypes.js';
import { Ask1stPartyEmailAuthData, GrantStatusResponseContent, OnLoginResponse, OnUserUpdateData, UserFullInfo } from '../types/UserDataTypes.js';
//import {pezzottoken} from './pezzottoken.js';



export interface APIClientImplConfig extends LayeredHTTPClientConfig {


    noAppAssociationHostName: string
    
}

/**
 * Questa classe si occupa di effettuare le richieste al server api di riferimento
 * e offre tutti gli stub per l'accesso diretto ai metodi remoti.
 *
 */
export class APIClientImpl extends RESTAPIEventsBaseImpl implements APIClientInterface {

    private client: LayeredAuthenticatedClient;

    constructor(
        private config: APIClientImplConfig,
        private logger: Logger
    ) {
        super();
        this.client = new LayeredAuthenticatedClient(this.config);
    }
    
    
    
   
    
    
   
    protected getNoAppAssociationUrl(): string {
        return "http" + (this.config.secure?"s":"") + "://" + this.config.noAppAssociationHostName + this.config.basePath;
    }

    private followStatusChange(originalQuery: TopicQueryObj, follow: boolean, response: Response<FollowResponseObject>): Response<FollowResponseObject> {
        this.triggerTopicFollowStatusChange({
            query: originalQuery,
            followed: follow,
            url: response.data?.topic.url
        });
        return response;
    }
    

    private userTopicFollowAction(originalQuery: TopicQueryObj, extra_info: UserActionExtraInfo | null, follow: boolean, response: Response<FollowResponseObject>): Response<FollowResponseObject> {
        this.triggerUserTopicFollowAction({
            query: originalQuery,
            followed: follow,
            url: response.data?.topic.url,
            extra_info: extra_info
        });
        return response;
    }


    private userNewsletterSubscriptionAction(originalQuery: TopicQueryObj, originalRestriction: ContentRestrictionCriteria,  extra_info: UserActionExtraInfo | null,subscribed: boolean, response: Response<TopicObj>): Response<TopicObj> {
        this.triggerUserNewsletterSubscribeAction({
            query: originalQuery,
            subscribed: subscribed,
            //url: response.data?.topic.url,
            extra_info: extra_info,
            restriction: originalRestriction
        });
        return response;
    }

        
    private triggerDeviceSubChange(deviceReq: DeviceUpdateRequestPayload|DeviceOrientedRequest, resp: SubscriptionListResponse, act: DeviceAction) {
        if (isFirebaseDeviceRequestPayload(deviceReq)) {
            this.triggerDeviceSubscriptionChange({
                action: act,
                device: deviceReq.firebase.device_type
            });
        }
        else if (!isDeviceUpdateRequestPayload(deviceReq)) {
            const found = resp.subscriptions.filter(ele => ele.token == deviceReq.token);
            if (found.length > 0) {
                this.triggerDeviceSubscriptionChange({
                    action: act,
                    device: found[0].deviceType
                });
            }
        }
    }


    private newsletterStatusChange(originalQuery: TopicQueryObj, originalRestriction: ContentRestrictionCriteria, subscribed: boolean, response: Response<TopicObj>): Response<TopicObj> {
        
        this.triggerNewsletterSubscriptionStatusChange({
            query: originalQuery,
            subscribed: subscribed,
            restriction: originalRestriction
        })
        return response;
    }



    
  
    
    

    public updateToken(tok: string): Promise<boolean> {
        this.client.setToken(tok);
        return Promise.resolve(true);
    }

    

    public flushToken(): Promise<boolean> {
        this.client.flushToken();
        return Promise.resolve(true);
    }

    /**
     * valid: false se il grant non è valido
     * @param token 
     * @returns 
     */
    public testGrant(token: string): Promise<Response<GrantStatusResponseContent>> {
        return this.client.get<GrantStatusResponseContent>("/rest/v1/auth/grant/test", { params: { access_token: token } });
    }

    public signIn(appToken: string, tpud: ThirdParyClientAuthDataForSignIn): Promise< Response<OnLoginResponse> > {
        return this.client.post<OnLoginResponse>("/rest/v1/auth/signin", tpud, { params: { access_token: appToken }, timeout: 5000, omitJWTAuth: true } );
    }


    public follow(queryObj: TopicQueryObj, extra_info: UserActionExtraInfo | null): Promise<Response<FollowResponseObject>> {
        return this.client.post<FollowResponseObject>("/rest/v1/notification/topic", queryObj).then(resp => {
            return this.followStatusChange(queryObj, true, resp);
        }).then(resp => {
            return this.userTopicFollowAction(queryObj, extra_info, true, resp);
        });
    }

    public unfollow(queryObj: TopicQueryObj, extra_info: UserActionExtraInfo | null): Promise<Response<FollowResponseObject>> {
        return this.client.delete<FollowResponseObject>("/rest/v1/notification/topic", queryObj).then(resp => {
            return this.followStatusChange(queryObj, false, resp);
        }).then(resp => {
            return this.userTopicFollowAction(queryObj, extra_info, false, resp);
        });
    }



    public checkFollowedTopics(checkReq: TopicFollowCheckRequest): Promise<Response<TopicFollowCheckResponse>> {
        return this.client.post<TopicFollowCheckResponse>("/rest/v1/notification/topic/check", checkReq).then(resp => {
            resp.data.topics.forEach(top => this.triggerTopicFollowStatusChange(top));
            return resp;
        });
    }
 


    subscribeNewsletter(queryObj: TopicQueryObj, extra_info: UserActionExtraInfo | null): Promise<Response<TopicObj>> {
        return this.client.post<TopicObj>("/rest/v1/newsletter/subscribe",queryObj).then(resp => {
            return this.newsletterStatusChange(queryObj,resp.data.subscriptionRestriction,resp.data.subscribed,resp)
        }).then(resp => {
            return this.userNewsletterSubscriptionAction(queryObj,resp.data.subscriptionRestriction,extra_info,resp.data.subscribed,resp);
        })
    }
    unsubscribeNewsletter(queryObj: TopicQueryObj, extra_info: UserActionExtraInfo | null): Promise<Response<TopicObj>> {
        return this.client.post<TopicObj>("/rest/v1/newsletter/unsubscribe",queryObj).then(resp => {
            return this.newsletterStatusChange(queryObj,resp.data.subscriptionRestriction,resp.data.subscribed,resp)
        }).then(resp => {
            return this.userNewsletterSubscriptionAction(queryObj,resp.data.subscriptionRestriction,extra_info,resp.data.subscribed,resp);
        })
    }

    removeNewsletter(queryObj: TopicQueryObj, extra_info: UserActionExtraInfo | null): Promise<Response<TopicObj>> {
        return this.client.delete<TopicObj>("/rest/v1/newsletter/subscription",queryObj).then(resp => {
                return this.newsletterStatusChange(queryObj,resp.data.subscriptionRestriction,resp.data.subscribed,resp)
        }).then(resp => {
            return this.userNewsletterSubscriptionAction(queryObj,resp.data.subscriptionRestriction,extra_info,resp.data.subscribed,resp);
        })
    }

    listNewsletterSubscriptions(): Promise<Response<TopicObj[]>> {
        return this.client.get<TopicObj[]>("/rest/v1/newsletter/subscriptions");
    }

    checkNewsletterSubscriptions(checkReq: TopicSubscriptionCheckRequest): Promise<Response<TopicSubscriptionCheckResponse>> {
        return this.client.post<TopicSubscriptionCheckResponse>("/rest/v1/newsletter/subscriptions/check", checkReq).then(resp => {
            resp.data.topics.forEach(top => this.triggerNewsletterSubscriptionStatusChange(top));
            return resp;
        });
    }



    public getMembershipStatusInfo(): Promise<MembershipStatusInfo> {
        return this.client.get<MembershipStatusInfo>("/rest/v1/membership/info").then(resp => {
            return resp.data;
        })
    }
    


    public getRecommended(skip: number = 0, limit: number = 10): Promise<Response<RecommendedResponse>> {
        return this.client.get<RecommendedResponse>("/rest/v1/notification/recommendations", {
            params: {
                skip: skip,
                limit: limit
            }
        });
    }

    public getRecommendedCount(): Promise<Response<RecommendedCount>> {
        return this.client.get<RecommendedCount>("/rest/v1/notification/recommendations/count");
    }


    public registerDevice(deviceReq: DeviceUpdateRequestPayload): Promise<Response<SubscriptionListResponse>> {
        return this.client.post<SubscriptionListResponse>("/rest/v1/subscription/device", deviceReq).then(
            resp => {
                this.triggerDeviceSubChange(deviceReq, resp.data, DeviceAction.SUBSCRIBE);
                return resp;
            }
        );
    }
    public unregisterDevice(deviceReq: DeviceOrientedRequest): Promise<Response<SubscriptionListResponse>> {
        return this.client.delete<SubscriptionListResponse>("/rest/v1/subscription/device", deviceReq).then(
            resp => {
                this.triggerDeviceSubChange(deviceReq, resp.data, DeviceAction.UNSUBSCRIBE);
                return resp;
            }
        );
    }


    public enableDevice(deviceReq: DeviceOrientedRequest): Promise<Response<SubscriptionListResponse>> {
        return this.client.post<SubscriptionListResponse>("/rest/v1/subscription/device/enable", deviceReq).then(
            resp => {
                this.triggerDeviceSubChange(deviceReq, resp.data, DeviceAction.ENABLE);
                return resp;
            }
        );
    }
    public disableDevice(deviceReq: DeviceOrientedRequest): Promise<Response<SubscriptionListResponse>> {
        return this.client.delete<SubscriptionListResponse>("/rest/v1/subscription/device/enable", deviceReq).then(
            resp => {
                this.triggerDeviceSubChange(deviceReq, resp.data, DeviceAction.DISABLE);
                return resp;
            }
        );
    }
    public listDevices(): Promise<Response<SubscriptionListResponse>> {
        return this.client.get<SubscriptionListResponse>("/rest/v1/subscription/devices");
    }

    
    

    public getMyPage(): Promise<Response<any>> {
        return this.client.get("/rest/v1/usercontent/mypage");
    }
    public getMyMenu(): Promise<Response<any>> {
        return this.client.get("/rest/v1/usercontent/mymenu");
    }

    public getUserFollowedTopics(): Promise<Response<GroupedResolvedTopicObjs>> {
        return this.client.get<GroupedResolvedTopicObjs>("/rest/v1/notification/topic/list");
    }


    public checkSavedArticles(checkReq: BookmarkCheckRequest): Promise<Response<BookmarkCheckResponse>> {
        return this.client.post<BookmarkCheckResponse>("/rest/v1/bookmark/content/check", checkReq).then(resp => {
            resp.data.contents.forEach(top => this.triggerArticleSaveStatusChange(top));
            return resp;
        });
    }
    public bookmark(q: BookmarkQueryObj, extra_info: UserActionExtraInfo | null): Promise<Response<BookmarkCheckResponse>> {
        return this.client.post<BookmarkCheckResponse>("/rest/v1/bookmark/content", q).then(resp => {
            resp.data.contents.forEach(top => {
                top.extra_info = extra_info;
                this.triggerArticleSaveStatusChange(top);
                this.triggerUserArticleSaveAction(top);
            });
            return resp;
        });
    }
    public unbookmark(q: BookmarkQueryObj, extra_info: UserActionExtraInfo | null): Promise<Response<BookmarkCheckResponse>> {
        return this.client.delete<BookmarkCheckResponse>("/rest/v1/bookmark/content", q).then(resp => {
            resp.data.contents.forEach(top => {
                top.extra_info = extra_info;
                this.triggerArticleSaveStatusChange(top);
                this.triggerUserArticleSaveAction(top);
            });
            return resp;
        });
    }
    public getSavedArticles(): Promise<Response<BookmarkedContentsResponse>> {
        return this.client.get<BookmarkedContentsResponse>("/rest/v1/bookmark/content/list");
    }

    public getSlider(sliderName: string, href?: string): Promise<Response<any>> {
        return this.client.get("/rest/v1/usercontent/slider/" + sliderName, {
            params: {
                ref: href
            }
        } );
    }


    public resolve(url: string): Promise<Response<FullContent>> {
        return this.client.get("/rest/0.5/resolve", {
            params: {
                url: url
            }
        });
    }
    public apiFreeCall(url: string): Promise<Response<any, any>> {
        const baseUrl = LayeredAuthenticatedClient.getBaseUrl({
            ...this.config
        });
        const urlDet = url.substring(0, baseUrl.length);
        const urlEp = url.substring(baseUrl.length);
        if(baseUrl == urlDet) {
            const oo = Utils.urlSep(urlEp);
            if(isObject(oo)) {
                return this.client.get(oo.url, { params: oo.params });
            }
            else {
                return Promise.reject("Invalid Url Passed");
            }
        }
        else {
            return Promise.reject("invalid host. Cannot proceed with freecall " + urlDet);
        }
        
    }


    public deletePersonalData(checkField: string): Promise<Response<DataDeletionInfo>> {
        return this.client.delete("/rest/v1/auth/personaldata", { check: checkField }, { timeout: 20000 });
    }
    

    public getMe(full_data: boolean): Promise<Response<UserFullInfo>> {
        return this.client.get("/rest/v1/user/me" + (full_data?"/full":"")).then(
            response => {
                return Response.OK<UserFullInfo>({
                    ...(response.data.nameAndGender),
                    ...response.data
                });
            }
        );
    }

    public resolveFollowedTopics(topics: GroupedTopicObjs): Promise<Response<GroupedResolvedTopicObjs>> {
        return this.client.post("/rest/v1/notification/topic/list/input", topics);
    }
    

    public updatePersonalData(upd: OnUserUpdateData): Promise<Response<OnLoginResponse>> {
        return this.client.post("/rest/v1/user/update", upd);
    }


    public becomeMember(): Promise<Response<any>> {
        return this.client.get("/membership/test/subscription");
    }

    public memberInfo(): Promise<Response<any>> {
        return this.client.get("/membership/info");
    }

    setMembershipCardData(firstName: string, lastName: string):Promise<Response<OnLoginResponse>> {
        return this.updatePersonalData({
            alternativeNaming: [firstName, lastName]
        });
    }

    generateMembershipCard():Promise<Response<void>> {
        return this.client.post("/membership/card/generate", {}, {
            timeout: 30000
        });
    }

    getMembershipCardData():Promise<MembershipCardData> {
        return this.client.get<MembershipCardData>("/membership/card/preview").then(x => x.data);
    }

    getMembershipBuyLink(): Promise<string> {
        return Promise.resolve(this.getNoAppAssociationUrl() + "/membership/wcm/redirect?action=buy");
    }

    getMembershipGiftLink(): Promise<string> {
        return Promise.resolve(this.getNoAppAssociationUrl() + "/membership/wcm/redirect?action=gift");
    }

    getRedeemGiftLink(subscriptionId: string, userId: string, checkId: string): Promise<string> {
        return Promise.resolve(this.getNoAppAssociationUrl() + "/membership/wcm/redirect?action=redeem&su="+subscriptionId+"&access_token=" + this.client.getToken() + "&u=" + userId + "&se=" +checkId);
    }

    getMembershipManagementLink(sub: string = null): Promise<string> {
        return Promise.resolve(this.getNoAppAssociationUrl() + "/membership/wcm/redirect?action=management&access_token=" + this.client.getToken() + (sub ? "&sub="+sub : ""));
    }

    getDirectBuyLink(prod: MembershipType, options: BuyMembershipOptions = {}): Promise<string> {
        let urlAlteration = "";
        if (options.returnUrl) {
            urlAlteration += "&redirect_url=" + encodeURIComponent(options.returnUrl);
        }
        if (options.useDiscount) {
            urlAlteration += "&coupon=InKESUTP9P45J2l2";
        }
        if (options.useFreeTrial) {
            urlAlteration += "&L7m80nPTdi=rGs2OwJXf9";
        }
        if (options.includeBook) {
            urlAlteration += "&includeBook=true";
        }
        if(options.ref) {
            urlAlteration += "&ref=" + encodeURIComponent(options.ref);
        }

        return Promise.resolve(this.getNoAppAssociationUrl() + "/membership/wcm/redirect?action=directBuy&prod=" + prod + urlAlteration);
    }

    getOrderPaymentLink(order: string, key: string): Promise<string> {
        return Promise.resolve(this.getNoAppAssociationUrl() + "/membership/wcm/redirect?action=payOrder&order=" + order + "&key=" + key);
    }
    

    getMembershipSwitchLink(redirectUrl: string = null) {
        return Promise.resolve(this.getNoAppAssociationUrl() + "/membership/wcm/redirect?action=switchPeriod" + (isString(redirectUrl) ? "&redirect_url=" + encodeURIComponent(redirectUrl) : ""));
    }

    getMembershipUpsellLink(options: MembershipUpsellOptions = {}) {
        let urlAlteration = "";
        if (options.returnUrl) {
            urlAlteration += "&redirect_url=" + encodeURIComponent(options.returnUrl);
        }
        if (options.targetMembershipType) {
            urlAlteration += "&prod=" + options.targetMembershipType;
        }
        if(options.ref) {
            urlAlteration += "&ref=" + encodeURIComponent(options.ref);
        }

        return Promise.resolve(this.getNoAppAssociationUrl() + "/membership/wcm/redirect?action=upsell" + urlAlteration);
    }
    
    hasMembershipAccess(): Promise<boolean> {
        return this.getMembershipDetails().then(x => x.hasMemershipAccess);
    }

    getMembershipDetails():Promise<MembershipDetails> {
        return this.getMe(true).then(
            me => {
                return {
                    hasMembershipBillingItems: me.data.hasMembershipBillingItems,
                    isMember: me.data.isMember,
                    hasMemershipAccess: me.data.hasMembershipBillingItems || me.data.isMember
                }
            }
        );
    }

    async ask1stPartyEmailLogin(operation: Ask1stPartyEmailLoginOperation, data: Ask1stPartyEmailAuthData): Promise<EmailLoginAskResponse> {
        const response = await this.client.post<EmailLoginAskResponse>("/rest/v1/email/" + operation, data);
        if(response.status == 200 && response.data && response.data.result) return response.data;
        else return Promise.reject("invalid answer")

    }

    async verificationWithOTPCode(challenge: string, code: string): Promise<OTPVerificationResponse> {
        const response = await this.client.post<OTPVerificationResponse>("/rest/v1/email/auth/codechallenge", {
            challenge: challenge,
            code: code,
            purpose: Ask1stPartyEmailLoginOperation.VERIFICATION
        });
        if(response.status == 200 && response.data) return response.data;
        else return Promise.reject("invalid answer")
    }
    
    getEventPlatformToken(): Promise<string> {
        return this.client.get("/eventplatform/token").then(res => {
            if(res.status == 200) {
                return res.data.token;
            }
            else {
                console.error("There was an error requiring token",res.status,res.data);
                return Promise.reject(res.data);
            }
        })
    }
}


export type APIClientMethod = keyof APIClientImpl;

export const AllAPIClientMethods: APIClientMethod[] = (AllRESTUserServicesMethods as APIClientMethod[]).concat(["signIn", "testGrant", "resolveFollowedTopics", "updatePersonalData", "updateToken", "flushToken", "deletePersonalData", "getMembershipBuyLink", "getMembershipGiftLink", "getRedeemGiftLink", "getMembershipManagementLink", "getDirectBuyLink","getMembershipStatusInfo", "hasMembershipAccess", "getMembershipDetails", "getMembershipCardData", "setMembershipCardData", "generateMembershipCard", "getMembershipSwitchLink", "getOrderPaymentLink", "getMembershipUpsellLink"]);
